import { useEffect, useState } from 'react';
import { API_URL } from '../config.js';

// Self-gating dev/test console panel.
//
// On mount it calls GET /api/dev/whoami. That route only exists on an instance
// running with ENABLE_DEV_TOOLS=true (staging) AND for a user with a dev role
// (tester/developer/superadmin). On production the route 404s, and for a
// non-dev user it 403s — in both cases this component renders NOTHING. So it is
// safe to mount unconditionally; it is invisible everywhere except the dev
// console for authorised users.
export default function DevToolsPanel({ onUnverified }) {
  const [info, setInfo] = useState(null);   // whoami .user, or null when unavailable
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const token = () => localStorage.getItem('token');

  async function loadWhoami() {
    try {
      const res = await fetch(`${API_URL}/dev/whoami`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) { setInfo(null); return; }   // 404 disabled / 403 no role → hide
      const data = await res.json();
      setInfo(data && data.user ? data.user : null);
    } catch {
      setInfo(null);
    }
  }

  useEffect(() => { loadWhoami(); }, []);

  if (!info) return null;   // not a dev instance, or not a dev user → render nothing

  async function unverify() {
    if (busy) return;
    setBusy(true); setMsg('');
    try {
      const res = await fetch(`${API_URL}/dev/unverify-self`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setMsg('Email un-verified — signing you out so you can re-run verification…');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setTimeout(() => {
          if (onUnverified) onUnverified(); else window.location.reload();
        }, 1000);
      } else {
        setMsg(data.message || 'Request failed');
        setBusy(false);
      }
    } catch {
      setMsg('Request failed'); setBusy(false);
    }
  }

  const box = {
    position: 'fixed', right: 16, bottom: 16, zIndex: 9999,
    fontFamily: 'ui-sans-serif, system-ui, sans-serif', fontSize: 13,
  };
  const chip = {
    background: '#3b2f00', color: '#ffd76a', border: '1px solid #6b5600',
    borderRadius: 8, padding: '8px 12px', cursor: 'pointer', userSelect: 'none',
    boxShadow: '0 4px 14px rgba(0,0,0,.35)', display: 'flex', alignItems: 'center', gap: 8,
  };
  const panel = {
    width: 300, background: '#141a24', color: '#e4e6eb',
    border: '1px solid #2a3441', borderRadius: 10, padding: 14,
    boxShadow: '0 8px 28px rgba(0,0,0,.5)',
  };
  const row = { display: 'flex', justifyContent: 'space-between', margin: '4px 0', color: '#9aa4b2' };
  const val = (ok) => ({ color: ok ? '#7ee787' : '#ff9f6a', fontWeight: 600 });

  return (
    <div style={box} data-testid="devtools">
      {!open ? (
        <div style={chip} onClick={() => setOpen(true)} data-testid="devtools-chip">
          🧪 Dev tools
        </div>
      ) : (
        <div style={panel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong style={{ color: '#ffd76a' }}>🧪 Dev / Test tools</strong>
            <span onClick={() => setOpen(false)} style={{ cursor: 'pointer', color: '#9aa4b2' }}>✕</span>
          </div>
          <div style={row}><span>Account</span><span style={{ color: '#e4e6eb' }}>{info.email}</span></div>
          <div style={row}><span>Role</span><span style={{ color: '#e4e6eb' }}>{info.devRole || info.globalRole}</span></div>
          <div style={row}>
            <span>Email verified</span>
            <span style={val(info.emailVerified)}>{info.emailVerified ? 'yes' : 'no'}</span>
          </div>
          <button
            onClick={unverify}
            disabled={busy}
            data-testid="unverify-btn"
            style={{
              marginTop: 10, width: '100%', padding: '9px 12px',
              background: busy ? '#3a3a3a' : '#a8323f', color: '#fff', border: 'none',
              borderRadius: 6, cursor: busy ? 'default' : 'pointer', fontWeight: 600,
            }}
          >
            {busy ? 'Working…' : 'Un-verify my email'}
          </button>
          <p style={{ margin: '8px 0 0', color: '#7a8594', fontSize: 12, lineHeight: 1.4 }}>
            Sets your email back to unverified and signs you out, so your next
            sign-in re-runs the email-verification flow.
          </p>
          {msg && <p data-testid="devtools-msg" style={{ margin: '8px 0 0', color: '#ffd76a', fontSize: 12 }}>{msg}</p>}
        </div>
      )}
    </div>
  );
}
